import type { MarketProject } from '../types/market';
import {
  DEFAULT_DOCKING_FILTER,
  type DockingFilter,
  type DockingRecord,
  type DockingStatistics,
  type ProjectDockingSummary,
} from '../types/docking';

export function isDockingFilterDirty(filter: DockingFilter): boolean {
  if (filter.category !== DEFAULT_DOCKING_FILTER.category) return true;
  if (filter.keyword?.trim()) return true;
  if (filter.maturity != null) return true;
  if (filter.dateFrom || filter.dateTo) return true;
  return false;
}

function matchesKeyword(record: DockingRecord, project: MarketProject, keyword: string): boolean {
  const k = keyword.toLowerCase();
  const parts = [
    record.projectName,
    record.counterpartyOrg,
    record.summary,
    record.ourParticipants.join(' '),
    record.counterpartyParticipants.join(' '),
    project.name,
    project.id,
    project.client,
    project.location,
    project.province,
    project.department,
  ];
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(k);
}

export function filterDockingRecords(
  records: DockingRecord[],
  projectsById: Map<string, MarketProject>,
  filter: DockingFilter,
): DockingRecord[] {
  return records.filter((record) => {
    const project = projectsById.get(record.projectId);
    if (!project) return false;
    if (filter.category !== 'all' && project.category !== filter.category) {
      return false;
    }
    if (filter.maturity != null && record.maturity !== filter.maturity) {
      return false;
    }
    if (filter.dateFrom && record.dockedAt < filter.dateFrom) return false;
    if (filter.dateTo && record.dockedAt > filter.dateTo) return false;
    const kw = filter.keyword?.trim();
    if (kw && !matchesKeyword(record, project, kw)) return false;
    return true;
  });
}

export function buildProjectDockingSummaries(
  records: DockingRecord[],
  projectsById: Map<string, MarketProject>,
): ProjectDockingSummary[] {
  const grouped = new Map<string, DockingRecord[]>();
  for (const record of records) {
    const list = grouped.get(record.projectId) ?? [];
    list.push(record);
    grouped.set(record.projectId, list);
  }

  const summaries: ProjectDockingSummary[] = [];
  grouped.forEach((list, projectId) => {
    const project = projectsById.get(projectId);
    if (!project) return;
    const sorted = [...list].sort((a, b) => b.dockedAt.localeCompare(a.dockedAt));
    summaries.push({
      projectId,
      project,
      recordCount: list.length,
      latestDockedAt: sorted[0].dockedAt,
      latestCounterpartyOrg: sorted[0].counterpartyOrg,
    });
  });

  summaries.sort((a, b) => b.latestDockedAt.localeCompare(a.latestDockedAt));
  return summaries;
}

export function countDockedProjectsByCategory(
  summaries: ProjectDockingSummary[],
): DockingStatistics {
  return {
    preliminary: summaries.filter((s) => s.project.category === 'preliminary').length,
    performance: summaries.filter((s) => s.project.category === 'performance').length,
  };
}

export function formatMaturityStars(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value <= 0) return '—';
  const n = Math.min(Math.round(value), 5);
  return `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;
}
